import { useState } from "react";
import { LookPresets } from "./LookPresets";
import { HeroFonts } from "./HeroFonts";
import { TEXT_ANIMS } from "../../lib/heroAnim";
import { api, errText, photoUrl } from "../../lib/api";
import { compressImage } from "../../lib/image";
import { Slider } from "../../components/ui/slider";
import { toast } from "sonner";
import { ImagePlus, Loader2, X } from "lucide-react";
import { torontoNow } from "../../lib/useSlideRotation";
import { GripVertical, Plus, Trash2, Copy, LayoutTemplate } from "lucide-react";
import { TemplatesGallery } from "./HeroTemplates";
import { Switch } from "../../components/ui/switch";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

export const newSlide = (base = {}) => ({
  id: `slide-${Math.random().toString(36).slice(2, 10)}`, enabled: true, duration: 6,
  badge: "", title_line1: "", title_line2: "", subtitle: "",
  badge_fr: "", title_line1_fr: "", title_line2_fr: "", subtitle_fr: "", start_at: "", end_at: "", buttons: [], bg_image: "", overlay: -1, line2_color: "", zoom: false, text_shadow: "off", bg_image_mobile: "", bg_focus: "", mobile_scale: 100, title_font: "", text_font: "", line1_color: "", badge_color: "", subtitle_color: "", badge_bg: "", desktop_scale: 100, btn_bg: "", btn_text: "", btn2_color: "", text_anim: "slide-up", ...base,
});

const fmtDay = (v) => {
  const d = new Date(`${v}:00`);
  return d.toLocaleDateString("en-CA", { month: "short", day: "numeric", ...(d.getFullYear() !== new Date().getFullYear() ? { year: "numeric" } : {}) });
};

export function scheduleState(s, now = torontoNow()) {
  if (s.end_at && now >= s.end_at) return { key: "ended", label: "Ended", cls: "bg-slate-200 text-slate-600" };
  if (s.start_at && now < s.start_at) return { key: "scheduled", label: `Scheduled · starts ${fmtDay(s.start_at)}`, cls: "bg-amber-100 text-amber-700" };
  if (s.end_at) return { key: "live", label: `Live until ${fmtDay(s.end_at)}`, cls: "bg-emerald-100 text-emerald-700" };
  return null;
}

export const isLiveSlide = (s) => s.enabled && !["ended", "scheduled"].includes(scheduleState(s)?.key);

const QUICK_LINKS = ["/browse", "/browse?category=lost-found", "/browse?category=lost-found&lf=reunited", "/post", "/post?category=lost-found&lf=lost", "/get-verified", "/advertise", "/contact"];

// Per-slide schedule window (Toronto time) and up to 2 call-to-action buttons.
export function SlideExtras({ slide, set }) {
  const btns = [0, 1].map((i) => (slide.buttons || [])[i] || { label: "", label_fr: "", link: "" });
  const setBtn = (i, k, v) => set("buttons", btns.map((b, j) => (j === i ? { ...b, [k]: v } : b)).filter((b, j) => j <= i || b.label || b.link));
  return (
    <div className="space-y-3">
      <div>
        <Label>Schedule <span className="text-slate-400 font-normal">(optional · Toronto time)</span></Label>
        <div className="mt-1 grid grid-cols-2 gap-2">
          <div><span className="text-[11px] text-slate-500">Starts</span><Input type="datetime-local" value={slide.start_at || ""} onChange={(e) => set("start_at", e.target.value)} data-testid="hero-slide-start" className="h-9 rounded-lg text-xs" /></div>
          <div><span className="text-[11px] text-slate-500">Ends</span><Input type="datetime-local" value={slide.end_at || ""} onChange={(e) => set("end_at", e.target.value)} data-testid="hero-slide-end" className="h-9 rounded-lg text-xs" /></div>
        </div>
        {(slide.start_at || slide.end_at) && <button type="button" onClick={() => { set("start_at", ""); set("end_at", ""); }} data-testid="hero-slide-clear-schedule" className="text-[11px] text-slate-500 hover:text-[#F5551E] mt-1">Clear schedule</button>}
      </div>
      <div>
        <Label>Buttons <span className="text-slate-400 font-normal">(optional · shown under the subtitle)</span></Label>
        <datalist id="hero-quick-links">{QUICK_LINKS.map((l) => <option key={l} value={l} />)}</datalist>
        {btns.map((b, i) => (
          <div key={i} className="mt-1.5 grid grid-cols-[auto_1fr_1fr] sm:grid-cols-[auto_1fr_1fr_1.3fr] gap-1.5 items-center" data-testid={`hero-btn-row-${i}`}>
            <span className={`h-6 px-2 rounded-md text-[10px] font-bold flex items-center ${i === 0 ? "bg-[#F5551E] text-white" : "border border-slate-300 text-slate-500"}`}>{i === 0 ? "Primary" : "Secondary"}</span>
            <Input value={b.label} onChange={(e) => setBtn(i, "label", e.target.value)} maxLength={40} placeholder="Label" data-testid={`hero-btn-label-${i}`} className="h-9 rounded-lg text-xs" />
            <Input value={b.label_fr} onChange={(e) => setBtn(i, "label_fr", e.target.value)} maxLength={40} placeholder="Label (FR)" data-testid={`hero-btn-label-fr-${i}`} className="h-9 rounded-lg text-xs" />
            <Input value={b.link} list="hero-quick-links" onChange={(e) => setBtn(i, "link", e.target.value)} placeholder="/browse?category=lost-found" data-testid={`hero-btn-link-${i}`} className="h-9 rounded-lg text-xs col-span-3 sm:col-span-1" />
          </div>
        ))}
        <p className="text-[11px] text-slate-400 mt-1">Links can be site pages (start with /) or full https:// addresses. Leave a label empty to hide that button.</p>
      </div>
      {(slide.buttons || []).length > 0 && (
        <div className="grid sm:grid-cols-3 gap-3 rounded-xl border border-slate-200 bg-white p-3" data-testid="hero-slide-btn-colors">
          <ColorPick slide={slide} set={set} k="btn_bg" label="Primary button" def="#F5551E" swatches={BTN_SWATCHES} id="btn-bg" />
          <ColorPick slide={slide} set={set} k="btn_text" label="Primary button text" def="#FFFFFF" swatches={LINE1_SWATCHES} id="btn-text" />
          <ColorPick slide={slide} set={set} k="btn2_color" label="Secondary (outline)" def="#FFFFFF" swatches={LINE1_SWATCHES} id="btn2" />
        </div>
      )}
    </div>
  );
}

const BTN_SWATCHES = [["#F5551E", "Brand orange"], ["#12234A", "Navy"], ["#FFFFFF", "White"], ["#16A34A", "Green"], ["#7C3AED", "Purple"], ["#E11D48", "Red"]];

export function TextAnimPicker({ slide, set, onReplay }) {
  const cur = slide.text_anim || "slide-up";
  return (
    <div className="sm:col-span-2" data-testid="hero-slide-anim">
      <div className="flex items-center justify-between">
        <Label>Text animation</Label>
        <button type="button" onClick={onReplay} className="text-[11px] font-semibold text-[#F5551E] hover:underline" data-testid="hero-anim-replay">Replay in preview</button>
      </div>
      <div className="mt-1.5 inline-flex flex-wrap rounded-xl bg-slate-100 p-1">
        {TEXT_ANIMS.map(([v, l]) => (
          <button key={v} type="button" onClick={() => { set("text_anim", v); onReplay?.(); }} data-testid={`hero-anim-${v}`}
            className={`h-7 px-3 rounded-lg text-xs font-semibold transition-colors ${cur === v ? "bg-white text-[#F5551E] shadow-sm" : "text-slate-500"}`}>{l}</button>
        ))}
      </div>
      <p className="text-[11px] text-slate-400 mt-1">How this slide's text appears each time it shows. Typewriter types the headline letter by letter.</p>
    </div>
  );
}

// Per-slide background photo (falls back to the main photo) and overlay strength.
export const LOOK_KEYS = ["line2_color", "text_shadow", "overlay", "zoom", "title_font", "text_font", "line1_color", "badge_color", "subtitle_color", "badge_bg", "btn_bg", "btn_text", "btn2_color", "text_anim"];

export function SlideLook({ slide, set, mainBg, others, onCopyAll, onReplay }) {
  const [busy, setBusy] = useState(false);
  const custom = slide.overlay != null && slide.overlay >= 0;
  const onFile = async (e, field = "bg_image") => {
    const file = (e.target.files || [])[0];
    e.target.value = "";
    if (!file) return;
    setBusy(field);
    try {
      const fd = new FormData();
      fd.append("file", await compressImage(file));
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set(field, data.path);
    } catch (err) { toast.error(errText(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid sm:grid-cols-2 gap-3" data-testid="hero-slide-look">
      <div>
        <Label>Slide background <span className="text-slate-400 font-normal">(optional)</span></Label>
        <div className="mt-1 flex items-center gap-2">
          <div className="relative h-12 w-20 rounded-lg overflow-hidden border border-slate-200 bg-slate-100 shrink-0">
            <img src={slide.bg_image ? photoUrl(slide.bg_image) : mainBg} alt="" className={`h-full w-full object-cover ${slide.bg_image ? "" : "opacity-50"}`} data-testid="hero-slide-bg-thumb" />
            {slide.bg_image && <button type="button" onClick={() => set("bg_image", "")} data-testid="hero-slide-bg-remove" className="absolute top-0.5 right-0.5 h-4 w-4 rounded-full bg-black/60 text-white flex items-center justify-center"><X size={10} /></button>}
          </div>
          <label data-testid="hero-slide-bg-upload" className="flex items-center gap-1 text-xs font-semibold text-[#F5551E] cursor-pointer hover:underline">
            {busy === "bg_image" ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />} {slide.bg_image ? "Replace" : "Upload photo"}
            <input type="file" accept="image/*" onChange={onFile} className="hidden" />
          </label>
        </div>
        <p className="text-[11px] text-slate-400 mt-1">{slide.bg_image ? "Fades in when this slide shows." : "Uses the main background photo."}</p>
      </div>
      <div>
        <div className="flex items-center justify-between">
          <Label>Overlay strength</Label>
          <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
            <span data-testid="hero-slide-overlay-value">{custom ? `${slide.overlay}%` : "Default"}</span>
            <Switch checked={custom} onCheckedChange={(on) => set("overlay", on ? 75 : -1)} data-testid="hero-slide-overlay-custom" />
          </div>
        </div>
        {custom
          ? <Slider value={[slide.overlay]} min={0} max={100} step={5} onValueChange={([v]) => set("overlay", v)} className="mt-4" data-testid="hero-slide-overlay-slider" />
          : <p className="text-[11px] text-slate-400 mt-2">Turn on to make the dark tint over the photo lighter or darker.</p>}
        {custom && <div className="mt-2 flex justify-between text-[10px] text-slate-400"><span>Lighter (more photo)</span><span>Darker</span></div>}
      </div>
      <LookPresets slide={slide} set={set} />
      <PhonePhoto slide={slide} set={set} mainBg={mainBg} busy={busy === "bg_image_mobile"} onFile={(e) => onFile(e, "bg_image_mobile")} />
      <PhoneTextSize slide={slide} set={set} />
      <DesktopTextSize slide={slide} set={set} />
      <TextAnimPicker slide={slide} set={set} onReplay={onReplay} />
      <HeroFonts slide={slide} set={set} />
      <TextColors slide={slide} set={set} />
      <div data-testid="hero-slide-shadow">
        <Label>Headline shadow</Label>
        <div className="mt-1.5 inline-flex rounded-xl bg-slate-100 p-1">
          {[["off", "Off"], ["soft", "Soft"], ["strong", "Strong"]].map(([v, l]) => (
            <button key={v} type="button" onClick={() => set("text_shadow", v)} data-testid={`hero-shadow-${v}`}
              className={`h-7 px-3 rounded-lg text-xs font-semibold transition-colors ${(slide.text_shadow || "off") === v ? "bg-white text-[#F5551E] shadow-sm" : "text-slate-500"}`}>{l}</button>
          ))}
        </div>
        <p className="text-[11px] text-slate-400 mt-1">Keeps the headline and subtitle readable on bright photos.</p>
      </div>
      <div className="flex items-start justify-between gap-3">
        <div><Label>Slow zoom</Label><p className="text-[11px] text-slate-400">A gentle zoom on the photo while this slide shows.</p></div>
        <Switch checked={!!slide.zoom} onCheckedChange={(on) => set("zoom", on)} data-testid="hero-slide-zoom" />
      </div>
      {others > 0 && (
        <button type="button" onClick={onCopyAll} data-testid="hero-copy-look" className="sm:col-span-2 h-9 rounded-xl border border-dashed border-[#F5551E]/50 text-xs font-semibold text-[#F5551E] hover:bg-[#FFF4F0] flex items-center justify-center gap-1.5">
          <Copy size={13} /> Copy this look to all other slides <span className="font-normal text-slate-400">(colours, shadow, overlay, zoom, fonts)</span>
        </button>
      )}
    </div>
  );
}

// Optional phone-only photo plus a tap-to-set focus point that stays in view on phones.
function PhonePhoto({ slide, set, mainBg, busy, onFile }) {
  const src = slide.bg_image_mobile ? photoUrl(slide.bg_image_mobile) : slide.bg_image ? photoUrl(slide.bg_image) : mainBg;
  const focus = slide.bg_focus || "50% 50%";
  const [fx, fy] = focus.split(" ").map((v) => parseInt(v, 10));
  const pickFocus = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = Math.round(((e.clientX - r.left) / r.width) * 100), y = Math.round(((e.clientY - r.top) / r.height) * 100);
    set("bg_focus", `${Math.max(0, Math.min(100, x))}% ${Math.max(0, Math.min(100, y))}%`);
  };
  return (
    <div className="sm:col-span-2 rounded-xl border border-slate-200 bg-white p-3" data-testid="hero-slide-phone">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Label>Phone photo <span className="text-slate-400 font-normal">(optional · tall photos work best)</span></Label>
        <div className="flex items-center gap-3">
          <label data-testid="hero-slide-phone-upload" className="flex items-center gap-1 text-xs font-semibold text-[#F5551E] cursor-pointer hover:underline">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />} {slide.bg_image_mobile ? "Replace" : "Upload phone photo"}
            <input type="file" accept="image/*" onChange={onFile} className="hidden" />
          </label>
          {slide.bg_image_mobile && <button type="button" onClick={() => set("bg_image_mobile", "")} data-testid="hero-slide-phone-remove" className="text-xs text-slate-500 hover:text-red-600">Remove</button>}
        </div>
      </div>
      <div className="mt-2 flex items-start gap-3">
        <div className="relative cursor-crosshair rounded-lg overflow-hidden border border-slate-200 shrink-0" onClick={pickFocus} data-testid="hero-slide-focus-picker" title="Tap the part that should stay in view on phones">
          <img src={src} alt="" className="block max-h-28 w-auto max-w-[200px]" draggable={false} />
          <span className="absolute h-4 w-4 -ml-2 -mt-2 rounded-full border-2 border-white bg-[#F5551E] shadow pointer-events-none" style={{ left: `${fx}%`, top: `${fy}%` }} />
        </div>
        <div className="shrink-0 h-28 w-14 rounded-lg border-2 border-[#12234A] overflow-hidden bg-slate-100" title="Phone preview">
          <img src={src} alt="" className="h-full w-full object-cover" style={{ objectPosition: focus }} data-testid="hero-slide-phone-preview" />
        </div>
        <p className="text-[11px] text-slate-400">{slide.bg_image_mobile ? "Phones show this photo instead of the desktop one." : "Phones show the desktop photo."} Tap the photo to choose what stays in view on phones{slide.bg_focus ? ` (now ${focus})` : ""}.
          {slide.bg_focus && <button type="button" onClick={() => set("bg_focus", "")} data-testid="hero-slide-focus-reset" className="ml-1 text-[#F5551E] hover:underline">Reset to centre</button>}</p>
      </div>
    </div>
  );
}

function PhoneTextSize({ slide, set }) {
  return <TextSize slide={slide} set={set} k="mobile_scale" id="phone" label="Phone text size" min={70} max={130} base={18} sub={9} />;
}

function DesktopTextSize({ slide, set }) {
  return <TextSize slide={slide} set={set} k="desktop_scale" id="desktop" label="Computer text size" min={70} max={150} base={22} sub={10} />;
}

function TextSize({ slide, set, k, id, label, min, max, base, sub }) {
  const v = slide[k] || 100;
  return (
    <div className="sm:col-span-2 rounded-xl border border-slate-200 bg-white p-3 flex items-start gap-4" data-testid={`hero-slide-${id}-text`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <Label>{label} <span className="text-slate-400 font-normal">(headline + subtitle)</span></Label>
          <span className="text-xs text-slate-500" data-testid={`hero-${id}-text-value`}>{v}%{v !== 100 && <button type="button" onClick={() => set(k, 100)} data-testid={`hero-${id}-text-reset`} className="ml-2 text-[#F5551E] hover:underline">Reset</button>}</span>
        </div>
        <Slider value={[v]} min={min} max={max} step={5} onValueChange={([x]) => set(k, x)} className="mt-4" data-testid={`hero-${id}-text-slider`} />
        <div className="mt-2 flex justify-between text-[10px] text-slate-400"><span>Smaller</span><span>Normal</span><span>Larger</span></div>
      </div>
      <div className="shrink-0 w-[150px] rounded-lg border-2 border-[#12234A] bg-[#12234A] p-2 overflow-hidden" title={`${label} preview`} data-testid={`hero-${id}-text-preview`}>
        <p className="font-display font-extrabold text-white leading-[1.05]" style={{ fontSize: base * v / 100 }}>{slide.title_line1 || "Find your next"}<br /><span style={{ color: slide.line2_color || "#F5551E" }}>{slide.title_line2 || "best friend."}</span></p>
        <p className="mt-1.5 text-slate-200 line-clamp-3" style={{ fontSize: sub * v / 100 }}>{slide.subtitle}</p>
      </div>
    </div>
  );
}

const BADGE_BG_SWATCHES = [["", "Glass (default)"], ["#F5551E", "Orange"], ["#12234A", "Navy"], ["#FFFFFF", "White"], ["#FBBF24", "Gold"], ["#4ADE80", "Green"], ["#7C3AED", "Purple"]];

const SWATCHES = [["#F5551E", "Brand orange"], ["#FFFFFF", "White"], ["#FBBF24", "Gold"], ["#7DD3FC", "Sky blue"], ["#4ADE80", "Green"], ["#F9A8D4", "Pink"]];

const LINE1_SWATCHES = [["#FFFFFF", "White"], ["#FDE68A", "Butter"], ["#FBCFE8", "Blush"], ["#BAE6FD", "Sky"], ["#12234A", "Navy"], ["#F5551E", "Orange"]];
const SUB_SWATCHES = [["#E2E8F0", "Soft white"], ["#FFFFFF", "White"], ["#CBD5E1", "Grey"], ["#FDE68A", "Butter"], ["#12234A", "Navy"]];

// Generic per-slide colour picker: swatches + custom colour. Empty value = the default colour.
function ColorPick({ slide, set, k, label, def, swatches, id }) {
  const cur = (slide[k] || def).toUpperCase();
  const choose = (c) => set(k, c.toUpperCase() === def.toUpperCase() ? "" : c);
  return (
    <div data-testid={`hero-slide-${id}-color`}>
      <Label>{label}</Label>
      <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
        {swatches.map(([c, name]) => (
          <button key={c || "default"} type="button" title={name} aria-label={name} onClick={() => choose(c)} data-testid={`hero-${id}-swatch-${c ? c.slice(1) : "default"}`}
            className={`h-7 w-7 rounded-full border border-slate-300 transition-transform hover:scale-110 ${cur === c ? "ring-2 ring-offset-2 ring-[#12234A]" : ""}`}
            style={{ background: c || "linear-gradient(135deg, #12234A 0 50%, #3A4A6E 50% 100%)" }} />
        ))}
        <label title="Custom colour" className={`relative h-7 w-7 rounded-full border border-slate-300 overflow-hidden cursor-pointer bg-[conic-gradient(red,yellow,lime,cyan,blue,magenta,red)] ${swatches.some(([c]) => c === cur) ? "" : "ring-2 ring-offset-2 ring-[#12234A]"}`}>
          <input type="color" value={(cur || "#FFFFFF").toLowerCase()} onChange={(e) => choose(e.target.value)} data-testid={`hero-${id}-custom`} className="absolute inset-0 opacity-0 cursor-pointer" />
        </label>
        <span className="text-[11px] text-slate-500 font-mono ml-1" data-testid={`hero-${id}-value`}>{cur || "Glass"}</span>
        {slide[k] && <button type="button" onClick={() => set(k, "")} className="text-[11px] text-[#F5551E] hover:underline" data-testid={`hero-${id}-reset`}>Default</button>}
      </div>
    </div>
  );
}

function TextColors({ slide, set }) {
  return (
    <div className="sm:col-span-2 grid sm:grid-cols-2 gap-4 rounded-xl border border-slate-200 bg-white p-3" data-testid="hero-slide-colors">
      <ColorPick slide={slide} set={set} k="line1_color" label="Headline line 1 colour" def="#FFFFFF" swatches={LINE1_SWATCHES} id="line1" />
      <LineColor slide={slide} set={set} />
      <ColorPick slide={slide} set={set} k="badge_color" label="Badge text colour" def="#FFFFFF" swatches={LINE1_SWATCHES} id="badge" />
      <ColorPick slide={slide} set={set} k="badge_bg" label="Badge background" def="" swatches={BADGE_BG_SWATCHES} id="badge-bg" />
      <ColorPick slide={slide} set={set} k="subtitle_color" label="Subtitle colour" def="#E2E8F0" swatches={SUB_SWATCHES} id="subtitle" />
    </div>
  );
}

function LineColor({ slide, set }) {
  const cur = (slide.line2_color || "#F5551E").toUpperCase();
  return (
    <div data-testid="hero-slide-line2-color">
      <Label>Headline line 2 colour</Label>
      <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
        {SWATCHES.map(([c, name]) => (
          <button key={c} type="button" title={name} aria-label={name} onClick={() => set("line2_color", c === "#F5551E" ? "" : c)} data-testid={`hero-line2-swatch-${c.slice(1)}`}
            className={`h-7 w-7 rounded-full border border-slate-300 transition-transform hover:scale-110 ${cur === c ? "ring-2 ring-offset-2 ring-[#12234A]" : ""}`} style={{ background: c }} />
        ))}
        <label title="Custom colour" className={`relative h-7 w-7 rounded-full border border-slate-300 overflow-hidden cursor-pointer bg-[conic-gradient(red,yellow,lime,cyan,blue,magenta,red)] ${SWATCHES.some(([c]) => c === cur) ? "" : "ring-2 ring-offset-2 ring-[#12234A]"}`}>
          <input type="color" value={cur.toLowerCase()} onChange={(e) => set("line2_color", e.target.value)} data-testid="hero-line2-custom" className="absolute inset-0 opacity-0 cursor-pointer" />
        </label>
        <span className="text-[11px] text-slate-500 font-mono ml-1" data-testid="hero-line2-value">{cur}</span>
      </div>
    </div>
  );
}

const MAX_SLIDES = 12;

// Admin list of rotating hero text slides: select, toggle, duration, duplicate, delete, drag-to-reorder.
export function SlidesList({ slides, setSlides, sel, setSel }) {
  const [drag, setDrag] = useState(null);
  const [tplOpen, setTplOpen] = useState(false);
  const addFromTemplate = ({ template_key, ...s }) => { setSlides((cur) => [...cur, s]); setSel(slides.length); };
  const update = (i, patch) => setSlides((s) => s.map((x, k) => (k === i ? { ...x, ...patch } : x)));
  const remove = (i) => { setSlides((s) => s.filter((_, k) => k !== i)); setSel((v) => Math.max(0, v >= i ? v - 1 : v)); };
  const dup = (i) => { setSlides((s) => { const { id, ...rest } = s[i]; return [...s.slice(0, i + 1), newSlide(rest), ...s.slice(i + 1)]; }); setSel(i + 1); };
  const add = () => { setSlides((s) => [...s, newSlide({ badge: "New slide", title_line1: "Your headline", title_line2: "goes here.", subtitle: "Supporting text for this slide." })]); setSel(slides.length); };
  const drop = (to) => {
    if (drag === null || drag === to) return setDrag(null);
    setSlides((s) => { const a = [...s]; const [m] = a.splice(drag, 1); a.splice(to, 0, m); return a; });
    setSel(to); setDrag(null);
  };

  return (
    <div className="space-y-2" data-testid="hero-slides-list">
      <div className="flex items-center justify-between">
        <Label>Rotating text slides <span className="text-slate-400 font-normal">({slides.filter((s) => s.enabled).length} on)</span></Label>
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => setTplOpen(true)} data-testid="hero-templates-btn" className="text-sm font-semibold text-[#12234A] flex items-center gap-1 hover:text-[#F5551E]"><LayoutTemplate size={15} /> Templates</button>
          <button type="button" onClick={add} disabled={slides.length >= MAX_SLIDES} data-testid="hero-slide-add" className="text-sm font-semibold text-[#F5551E] flex items-center gap-1 hover:underline disabled:opacity-40"><Plus size={15} /> Add slide</button>
        </div>
        <TemplatesGallery open={tplOpen} onOpenChange={setTplOpen} onPick={addFromTemplate} disabled={slides.length >= MAX_SLIDES} />
      </div>
      {slides.map((s, i) => (
        <div key={s.id} draggable onDragStart={() => setDrag(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => drop(i)} onDragEnd={() => setDrag(null)}
          onClick={() => setSel(i)} data-testid={`hero-slide-${i}`}
          className={`flex items-center gap-2 rounded-xl border p-2 cursor-pointer transition-colors ${sel === i ? "border-[#F5551E] bg-[#FFF4F0]" : "border-slate-200 hover:border-slate-300"} ${drag === i ? "opacity-50" : ""} ${s.enabled ? "" : "opacity-60"}`}>
          <GripVertical size={16} className="text-slate-400 cursor-grab shrink-0" data-testid={`hero-slide-drag-${i}`} />
          <span className="h-6 w-6 rounded-full bg-[#12234A] text-white text-[11px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[#12234A] truncate">{s.title_line1} <span className="text-[#F5551E]">{s.title_line2}</span></p>
            <p className="text-[11px] text-slate-400 truncate flex items-center gap-1.5">
              {scheduleState(s) && <span data-testid={`hero-slide-schedule-${i}`} className={`shrink-0 px-1.5 py-px rounded font-semibold ${scheduleState(s).cls}`}>{scheduleState(s).label}</span>}
              <span className="truncate">{s.badge}</span>
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
            <Input type="number" min={2} max={60} value={s.duration} onChange={(e) => update(i, { duration: e.target.value === "" ? "" : Number(e.target.value) })}
              onBlur={() => update(i, { duration: Math.max(2, Math.min(60, Number(s.duration) || 6)) })}
              data-testid={`hero-slide-duration-${i}`} className="h-8 w-14 rounded-lg text-xs px-2" title="Seconds on screen" />
            <span className="text-[11px] text-slate-400 mr-1">s</span>
            <Switch checked={s.enabled} onCheckedChange={(v) => update(i, { enabled: v })} data-testid={`hero-slide-toggle-${i}`} />
            <button type="button" onClick={() => dup(i)} disabled={slides.length >= MAX_SLIDES} title="Duplicate" data-testid={`hero-slide-dup-${i}`} className="h-8 w-8 rounded-lg text-slate-400 hover:text-[#12234A] hover:bg-slate-100 flex items-center justify-center disabled:opacity-40"><Copy size={14} /></button>
            <button type="button" onClick={() => remove(i)} disabled={slides.length <= 1} title="Delete" data-testid={`hero-slide-delete-${i}`} className="h-8 w-8 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center disabled:opacity-30"><Trash2 size={14} /></button>
          </div>
        </div>
      ))}
      <p className="text-[11px] text-slate-400">Drag to reorder. Each slide stays on screen for its own number of seconds (2–60). Rotation pauses while a visitor is typing in the search box.</p>
    </div>
  );
}
